/**
 * Timing core behind `useHoverIntent`, kept free of React so it can be unit
 * tested in the node environment the vitest config uses.
 *
 * The point of the delay is that a cursor merely travelling across the report
 * must not light up every panel it crosses. But once *some* chrome is up,
 * walking from one block to the next has to feel instant, so warmth is shared
 * across every controller: while anything is revealed — or was revealed a
 * moment ago — the next reveal skips the delay entirely. Same idea as Radix's
 * `skipDelayDuration`, applied to the editor's hover chrome as a whole.
 */

/** Cold reveal delay: long enough that a sweeping cursor triggers nothing. */
export const HOVER_INTENT_SHOW_MS = 200;
/**
 * Grace period before hiding. Chrome is routinely positioned outside the
 * element that reveals it (the section toolbar overflows below short blocks,
 * the block rail sits in the right gutter), so travelling onto it crosses a
 * dead zone this bridges.
 */
export const HOVER_INTENT_HIDE_MS = 250;
/** How long the "instant reveal" window survives after the last chrome hides. */
export const HOVER_INTENT_WARM_MS = 400;

let activeCount = 0;
let warmUntil = 0;

function isWarm(): boolean {
    return activeCount > 0 || Date.now() < warmUntil;
}

/** Test-only: drop the shared warmth so specs start cold. */
export function resetHoverIntentWarmth(): void {
    activeCount = 0;
    warmUntil = 0;
}

export type HoverIntentConfig = {
    showDelay: number;
    hideDelay: number;
    disabled: boolean;
};

export type HoverIntentController = {
    enter(): void;
    leave(): void;
    showNow(): void;
    hideNow(): void;
    setKeepOpen(keepOpen: boolean): void;
    configure(config: Partial<HoverIntentConfig>): void;
    isActive(): boolean;
    isDisposed(): boolean;
    dispose(): void;
};

export function createHoverIntentController(
    onChange: (active: boolean) => void,
    initial: Partial<HoverIntentConfig> = {},
): HoverIntentController {
    const config: HoverIntentConfig = {
        showDelay: initial.showDelay ?? HOVER_INTENT_SHOW_MS,
        hideDelay: initial.hideDelay ?? HOVER_INTENT_HIDE_MS,
        disabled: initial.disabled ?? false,
    };

    let active = false;
    let pointerInside = false;
    let keepOpen = false;
    let disposed = false;
    let showTimer: ReturnType<typeof setTimeout> | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const clearShow = () => {
        if (showTimer !== null) {
            clearTimeout(showTimer);
            showTimer = null;
        }
    };

    const clearHide = () => {
        if (hideTimer !== null) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
    };

    const activate = () => {
        if (active || disposed) return;
        active = true;
        activeCount += 1;
        onChange(true);
    };

    const deactivate = (notify: boolean) => {
        if (!active) return;
        active = false;
        activeCount -= 1;
        if (activeCount <= 0) {
            activeCount = 0;
            warmUntil = Date.now() + HOVER_INTENT_WARM_MS;
        }
        if (notify) onChange(false);
    };

    const scheduleHide = () => {
        if (!active || keepOpen) return;
        clearHide();
        hideTimer = setTimeout(() => {
            hideTimer = null;
            deactivate(true);
        }, config.hideDelay);
    };

    return {
        enter() {
            if (disposed) return;
            pointerInside = true;
            clearHide();
            if (config.disabled || active) return;
            if (isWarm()) {
                clearShow();
                activate();
                return;
            }
            if (showTimer !== null) return;
            showTimer = setTimeout(() => {
                showTimer = null;
                activate();
            }, config.showDelay);
        },

        leave() {
            if (disposed) return;
            pointerInside = false;
            clearShow();
            scheduleHide();
        },

        showNow() {
            if (disposed || config.disabled) return;
            clearShow();
            clearHide();
            activate();
        },

        hideNow() {
            if (disposed) return;
            clearShow();
            clearHide();
            deactivate(true);
        },

        setKeepOpen(next) {
            if (disposed || keepOpen === next) return;
            keepOpen = next;
            if (keepOpen) {
                clearHide();
                return;
            }
            // Released mid-gesture with the pointer already gone: fall back to
            // the normal grace period rather than snapping shut.
            if (!pointerInside) scheduleHide();
        },

        configure(next) {
            if (next.showDelay !== undefined) config.showDelay = next.showDelay;
            if (next.hideDelay !== undefined) config.hideDelay = next.hideDelay;
            if (next.disabled !== undefined) {
                config.disabled = next.disabled;
                if (config.disabled) {
                    clearShow();
                    clearHide();
                    deactivate(true);
                }
            }
        },

        isActive() {
            return active;
        },

        isDisposed() {
            return disposed;
        },

        dispose() {
            if (disposed) return;
            clearShow();
            clearHide();
            deactivate(false);
            disposed = true;
        },
    };
}
